import React, { useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Popover, Stack, Tooltip } from '@mui/material';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { TFunction } from 'i18next';
import type { ChatReactionEmoji, ChatReactionEntry } from '../../models/Chat';
import { CHAT_EMOJI_CATEGORIES, CHAT_REACTION_EMOJI_LABELS, CHAT_REACTION_QUICK_EMOJIS } from '../../utils/chatEmojis';
import { isViewerReaction } from '../../utils/chatReactions';
import { ChatEmojiPickerPanel } from './ChatEmojiPicker';

interface ReactionPickerPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  reactionEmoji: ChatReactionEmoji | null;
  t: TFunction;
  onClose: () => void;
  onSelect: (emoji: ChatReactionEmoji) => void;
  onClear: () => void;
}

function ReactionPickerPopover({
  open,
  anchorEl,
  reactionEmoji,
  t,
  onClose,
  onSelect,
  onClear,
}: ReactionPickerPopoverProps) {
  const [showMore, setShowMore] = useState(false);

  const handleSelect = (emoji: string) => {
    if (reactionEmoji === emoji) {
      onClear();
    } else {
      onSelect(emoji);
    }
    setShowMore(false);
    onClose();
  };

  const handleClose = () => {
    setShowMore(false);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionProps={{
        onExited: () => setShowMore(false),
      }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: 8,
            borderRadius: 2,
            width: showMore ? 320 : 'auto',
            maxWidth: 'calc(100vw - 24px)',
            overflow: 'hidden',
          },
        },
      }}
    >
      {showMore ? (
        <ChatEmojiPickerPanel
          t={t}
          categories={CHAT_EMOJI_CATEGORIES}
          selectedEmoji={reactionEmoji}
          onSelect={handleSelect}
        />
      ) : (
        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ px: 0.75, py: 0.5 }}>
          <Box
            role="listbox"
            aria-label={t('chat.timeline.reactionPicker', 'Reaction picker')}
            sx={{ display: 'flex', gap: 0.25 }}
          >
            {CHAT_REACTION_QUICK_EMOJIS.map((emoji) => {
              const selected = reactionEmoji === emoji;
              const meaning = CHAT_REACTION_EMOJI_LABELS[emoji];
              const label = t(meaning.key, meaning.defaultLabel);
              return (
                <Tooltip key={emoji} title={label}>
                  <IconButton
                    role="option"
                    aria-label={label}
                    aria-selected={selected}
                    onClick={() => handleSelect(emoji)}
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: '1.2rem',
                      borderRadius: 1,
                      bgcolor: selected ? 'rgba(255,255,255,0.12)' : 'transparent',
                      outline: selected ? '1px solid' : 'none',
                      outlineColor: 'primary.main',
                    }}
                  >
                    {emoji}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>

          <Button
            size="small"
            onClick={() => setShowMore(true)}
            aria-label={t('chat.timeline.moreReactions', 'More')}
            sx={{
              minWidth: 0,
              px: 1,
              height: 36,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: 'text.primary' },
            }}
          >
            {t('chat.timeline.moreReactions', 'More')}
          </Button>

          {reactionEmoji && (
            <Tooltip title={t('chat.timeline.clearReaction', 'Clear reaction')}>
              <IconButton
                size="small"
                onClick={() => {
                  onClear();
                  handleClose();
                }}
                aria-label={t('chat.timeline.clearReaction', 'Clear reaction')}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: 'text.primary' },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Popover>
  );
}

interface ChatMessageReactionProps {
  reactionEmoji: ChatReactionEmoji | null;
  busy?: boolean;
  t: TFunction;
  onSelect: (emoji: ChatReactionEmoji) => void;
  onClear: () => void;
}

/** Hover-toolbar add-reaction control with curated emoji picker. */
const ChatMessageReaction: React.FC<ChatMessageReactionProps> = ({
  reactionEmoji,
  busy = false,
  t,
  onSelect,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const triggerLabel = t('chat.timeline.addReaction', 'Add reaction');

  return (
    <>
      <Tooltip title={triggerLabel}>
        <span>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
            disabled={busy}
            aria-label={triggerLabel}
            aria-expanded={open}
            aria-haspopup="dialog"
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: 'text.primary' },
            }}
          >
            {busy ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <AddReactionOutlinedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <ReactionPickerPopover
        open={open}
        anchorEl={anchorEl}
        reactionEmoji={reactionEmoji}
        t={t}
        onClose={() => setAnchorEl(null)}
        onSelect={onSelect}
        onClear={onClear}
      />
    </>
  );
};

interface ChatMessageReactionBadgesProps {
  reactions: ChatReactionEntry[];
  reactionEmoji: ChatReactionEmoji | null;
  currentUserId?: string;
  busy?: boolean;
  t: TFunction;
  onSelect: (emoji: ChatReactionEmoji) => void;
  onClear: () => void;
}

/** Shared reaction chips — one badge per user reaction; viewer's chip opens the picker. */
export const ChatMessageReactionBadges: React.FC<ChatMessageReactionBadgesProps> = ({
  reactions,
  reactionEmoji,
  currentUserId,
  busy = false,
  t,
  onSelect,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const addLabel = t('chat.timeline.addReaction', 'Add reaction');

  if (!reactions.length) return null;

  const openPicker = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  // One reaction per viewer; keep the inline add control while the viewer has none.
  const showInlineAdd = !reactionEmoji;

  return (
    <>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.75 }}>
        {reactions.map((reaction) => {
          const mine = isViewerReaction(reaction, currentUserId);
          const chip = (
            <IconButton
              size="small"
              disabled={busy || !mine}
              aria-label={
                mine ? t('chat.timeline.changeReaction', 'Change reaction') : reaction.emoji
              }
              aria-pressed={mine || undefined}
              aria-expanded={mine && open ? true : undefined}
              aria-haspopup={mine ? 'dialog' : undefined}
              onClick={mine ? openPicker : undefined}
              sx={{
                width: 'auto',
                minWidth: 28,
                height: 26,
                px: 0.85,
                borderRadius: 2,
                fontSize: '0.9rem',
                color: 'text.primary',
                cursor: mine ? 'pointer' : 'default',
                bgcolor: mine ? 'rgba(78, 103, 235, 0.18)' : 'rgba(255,255,255,0.06)',
                border: 1,
                borderColor: mine ? 'primary.main' : 'divider',
                '&.Mui-disabled': {
                  opacity: 1,
                  color: 'text.primary',
                },
                '&:hover': mine
                  ? {
                      bgcolor: 'rgba(78, 103, 235, 0.28)',
                      borderColor: 'primary.light',
                    }
                  : {
                      bgcolor: 'rgba(255,255,255,0.06)',
                      borderColor: 'divider',
                    },
              }}
            >
              {busy && mine ? <CircularProgress size={12} color="inherit" /> : reaction.emoji}
            </IconButton>
          );

          return (
            <React.Fragment key={`${reaction.userId}-${reaction.emoji}`}>
              {mine ? (
                <Tooltip title={t('chat.timeline.changeReaction', 'Change reaction')}>
                  <span>{chip}</span>
                </Tooltip>
              ) : (
                chip
              )}
            </React.Fragment>
          );
        })}

        {showInlineAdd && (
          <Tooltip title={addLabel}>
            <span>
              <IconButton
                size="small"
                disabled={busy}
                aria-label={addLabel}
                aria-expanded={open}
                aria-haspopup="dialog"
                onClick={openPicker}
                sx={{
                  width: 28,
                  height: 26,
                  borderRadius: 2,
                  color: 'text.secondary',
                  bgcolor: 'transparent',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: 'text.primary',
                    borderColor: 'rgba(255,255,255,0.25)',
                  },
                }}
              >
                <AddReactionOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      <ReactionPickerPopover
        open={open}
        anchorEl={anchorEl}
        reactionEmoji={reactionEmoji}
        t={t}
        onClose={() => setAnchorEl(null)}
        onSelect={onSelect}
        onClear={onClear}
      />
    </>
  );
};

export default ChatMessageReaction;
